import { useRef, useEffect, useState, useMemo } from 'react'
import { CameraHelper } from 'three'
import { Instances, Model as BlackArrow } from './models/black_arrow.jsx'
import { Instances as GantryInstances, Model as Gantry } from './models/gantry.jsx'
import { useThree, useFrame } from '@react-three/fiber'
import { useAnimation } from './animations.jsx'
import FadingGround from './FadingGround.jsx'
import TripleSceneManager from './TripleSceneManager.jsx'
import * as THREE from 'three'
import { Environment, CubeCamera, BakeShadows } from '@react-three/drei'

function ScreenPlane({ texture1, texture2, texture3, fadeProgress, scene3Progress, selectiveBloomEnabled = false }) {
  const { camera, viewport } = useThree()
  const meshRef = useRef()
  
  // Color grading values (hardcoded)
  const colorGrading = {
    exposure: 1.0,
    contrast: 1.0,
    saturation: 1.0,
    brightness: 0.0,
    highlights: 1.0,
    shadows: 0.71,
    warmth: 0.10,
    tint: 0.10,
  }
  
  // Create shader material with useMemo
  const shaderMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      transparent: true,
      depthTest: false,
      uniforms: {
        texture1: { value: texture1 },
        texture2: { value: texture2 },
        texture3: { value: texture3 },
        fadeProgress: { value: fadeProgress },
        scene3Progress: { value: scene3Progress },
        // Color grading controls
        exposure: { value: 1.0 },
        contrast: { value: 1.0 },
        saturation: { value: 1.0 },
        brightness: { value: 0.0 },
        highlights: { value: 1.0 },
        shadows: { value: 1.0 },
        warmth: { value: 0.0 },
        tint: { value: 0.0 },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D texture1;
        uniform sampler2D texture2;
        uniform sampler2D texture3;
        uniform float fadeProgress;
        uniform float scene3Progress;
        uniform float exposure;
        uniform float contrast;
        uniform float saturation;
        uniform float brightness;
        uniform float highlights;
        uniform float shadows;
        uniform float warmth;
        uniform float tint;
        varying vec2 vUv;
        
        // Convert RGB to luminance
        float getLuminance(vec3 color) {
          return dot(color, vec3(0.299, 0.587, 0.114));
        }
        
        // Adjust saturation
        vec3 adjustSaturation(vec3 color, float sat) {
          float lum = getLuminance(color);
          return mix(vec3(lum), color, sat);
        }
        
        // Adjust contrast
        vec3 adjustContrast(vec3 color, float cont) {
          return (color - 0.5) * cont + 0.5;
        }
        
        // Apply color temperature (warmth) and tint
        vec3 applyWhiteBalance(vec3 color, float temp, float tintValue) {
          // Warmth: negative = cooler (blue), positive = warmer (orange)
          // Tint: negative = green, positive = magenta
          
          // Temperature adjustment (blue-orange axis)
          vec3 warmColor = color;
          if (temp > 0.0) {
            // Warmer: boost red/yellow, reduce blue
            warmColor.r += temp * 0.3;
            warmColor.g += temp * 0.15;
            warmColor.b -= temp * 0.2;
          } else {
            // Cooler: reduce red, boost blue
            warmColor.r += temp * 0.2;
            warmColor.b -= temp * 0.3;
          }
          
          // Tint adjustment (green-magenta axis)
          if (tintValue > 0.0) {
            // Magenta: boost red and blue
            warmColor.r += tintValue * 0.2;
            warmColor.b += tintValue * 0.2;
            warmColor.g -= tintValue * 0.1;
          } else {
            // Green: boost green
            warmColor.g -= tintValue * 0.3;
          }
          
          return warmColor;
        }
        
        // Adjust highlights and shadows
        vec3 adjustHighlightsShadows(vec3 color, float highlightMult, float shadowMult) {
          float lum = getLuminance(color);
          
          // Smooth transition between shadows and highlights
          float shadowFactor = smoothstep(0.0, 0.5, lum);
          float highlightFactor = smoothstep(0.5, 1.0, lum);
          
          vec3 result = color;
          result = mix(result * shadowMult, result, shadowFactor);
          result = mix(result, result * highlightMult, highlightFactor);
          
          return result;
        }
        
        void main() {
          vec4 color1 = texture2D(texture1, vUv);
          vec4 color2 = texture2D(texture2, vUv);
          vec4 color3 = texture2D(texture3, vUv);
          
          // Apply color grading to scene 1 only
          vec4 gradedColor1 = color1;
          
          // Apply exposure
          gradedColor1.rgb *= exposure;
          
          // Apply white balance (warmth and tint)
          gradedColor1.rgb = applyWhiteBalance(gradedColor1.rgb, warmth, tint);
          
          // Apply highlights and shadows
          gradedColor1.rgb = adjustHighlightsShadows(gradedColor1.rgb, highlights, shadows);
          
          // Apply brightness
          gradedColor1.rgb += brightness;
          
          // Apply saturation
          gradedColor1.rgb = adjustSaturation(gradedColor1.rgb, saturation);
          
          // Apply contrast
          gradedColor1.rgb = adjustContrast(gradedColor1.rgb, contrast);
          
          // Three-way blend:
          // fadeProgress: 0=Scene1, 1=Scene2
          // scene3Progress: 0=Scene2, 1=Scene3
          // First blend Scene1 -> Scene2
          vec4 scene1to2 = mix(gradedColor1, color2, fadeProgress);
          // Then blend Scene2 -> Scene3
          vec4 finalColor = mix(scene1to2, color3, scene3Progress);
          
          gl_FragColor = finalColor;
        }
      `
    })
  }, [texture1, texture2, texture3])
  
  // Handle selective bloom layer assignment
  useEffect(() => {
    if (meshRef.current) {
      if (selectiveBloomEnabled) {
        meshRef.current.layers.enable(1) // Enable layer 1 for selective bloom
      } else {
        meshRef.current.layers.disable(1) // Disable layer 1
      }
    }
  }, [selectiveBloomEnabled])
  
  useFrame(() => {
    if (meshRef.current) {
      // Position plane in front of camera
      meshRef.current.position.copy(camera.position)
      meshRef.current.quaternion.copy(camera.quaternion)
      meshRef.current.translateZ(-5)
    }
    
    // Update uniforms every frame
    if (shaderMaterial && shaderMaterial.uniforms) {
      shaderMaterial.uniforms.fadeProgress.value = fadeProgress
      shaderMaterial.uniforms.scene3Progress.value = scene3Progress
      shaderMaterial.uniforms.texture1.value = texture1
      shaderMaterial.uniforms.texture2.value = texture2
      shaderMaterial.uniforms.texture3.value = texture3
      
      // Update color grading uniforms
      shaderMaterial.uniforms.exposure.value = colorGrading.exposure
      shaderMaterial.uniforms.contrast.value = colorGrading.contrast
      shaderMaterial.uniforms.saturation.value = colorGrading.saturation
      shaderMaterial.uniforms.brightness.value = colorGrading.brightness
      shaderMaterial.uniforms.highlights.value = colorGrading.highlights
      shaderMaterial.uniforms.shadows.value = colorGrading.shadows
      shaderMaterial.uniforms.warmth.value = colorGrading.warmth
      shaderMaterial.uniforms.tint.value = colorGrading.tint
    }
  })
  
  return (
    <mesh ref={meshRef} material={shaderMaterial}>
      <planeGeometry args={[viewport.width, viewport.height]} />
    </mesh>
  )
}

function TripleSceneContents({ showHelpers = false, selectiveBloomEnabled = false, onScenesReady, isAtPOI = false, activePOI = null, cameraZoom = 2000, cameraRotation = 0 }) {
  const { hdriBlend, earthEnvBlend, scene3Blend, earthRotation } = useAnimation()
  const dirLight1Ref = useRef()
  const cameraHelper1Ref = useRef()
  const { gl } = useThree()
  
  const [scene1Texture, setScene1Texture] = useState(null)
  const [scene2Texture, setScene2Texture] = useState(null)
  const [scene3Texture, setScene3Texture] = useState(null)
  
  // Track animation state
  const [isAnimating, setIsAnimating] = useState(false)
  
  // Ref to sceneControls to avoid triggering effect on every render
  const sceneControlsRef = useRef(null)
  
  // Ref to allowCameraControl so we can read current value in closures without re-triggering effects
  const allowCameraControlRef = useRef(true)
  
  // Control when BakeShadows renders (delay by 1 second after mount)
  const [shouldBakeShadows, setShouldBakeShadows] = useState(false)
  
  // Delay BakeShadows by 1 second - only runs once on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldBakeShadows(true)
    }, 1000)
    
    return () => clearTimeout(timer)
  }, [])
  
  // Preload Earth environment textures when in Scene 2
  useEffect(() => {
    if (hdriBlend >= 1) {
      // Preload Earth textures in the background
      const img1 = new Image()
      const img2 = new Image()
      img1.src = '/textures/hdri/earth2.webp'
      img2.src = '/textures/hdri/earth2-gainmap.webp'
    }
  }, [hdriBlend])
  
  // Calculate POI and TAR positions based on gantry position
  const calculatePoiTarPositions = (gantryPosition, gantryRotation) => {
    const gantryPos = new THREE.Vector3(...gantryPosition)
    const gantryRot = new THREE.Euler(...gantryRotation)
    
    // Local positions from the model (relative to their parent groups)
    const localPositions = {
      // Clamp (relative to gantry root)
      POI_Clamp: new THREE.Vector3(1.261, 1.681, -1.247),
      POS_ClampCam: new THREE.Vector3(9.373, 4.719, -4.965),
      TAR_Clamp: new THREE.Vector3(1.455, 1.395, 0.198),
      
      // Fuel (relative to Fuel mesh at [-3, 1.055, -3] in gantry space)
      POI_Fuel: new THREE.Vector3(-3 + 0.189, 1.055 + 6.969, -3 + (-0.409)),
      POS_FuelCam: new THREE.Vector3(-3 + 7.793, 1.055 + 12.331, -3 + (-7.84)),
      TAR_Fuel: new THREE.Vector3(-3 + 1.227, 1.055 + 8.173, -3 + 0.837),
      
      // Shack (relative to Shack group at [-0.75, 0, 24.125] in gantry space)
      POI_Shack: new THREE.Vector3(-0.75 + (-1.359), 0 + 5.712, 24.125 + (-1.625)),
      POS_ShackCam: new THREE.Vector3(-0.75 + 23.753, 0 + 19.023, 24.125 + (-28.085)),
      TAR_Shack: new THREE.Vector3(-0.75 + (-2.938), 0 + 9.016, 24.125 + (-1.924))
    }
    
    // Apply gantry transformation to get world positions
    const worldPositions = {}
    Object.entries(localPositions).forEach(([key, localPos]) => {
      const worldPos = localPos.clone()
      worldPos.applyEuler(gantryRot)
      worldPos.add(gantryPos)
      worldPositions[key] = worldPos
    })
    
    return worldPositions
  }
  
  // HDRI background intensities (hardcoded)
  const hdriControls = {
    backgroundIntensity: 1.0,
    environmentIntensity: 2.0,
  }
  
  // Hardcoded light values
  const lightControls = {
    lightIntensity: 5.0,
    lightColor: '#ffeee1',
    showShadowHelper: false,
    shadowBias: -0.0024,
    shadowRadius: 1,
  }
  
  // hdriBlend goes from 0 to 1, use this as our fade progress
  // Scene 1: Only mount when hdriBlend is less than 1 (unmount after fade completes)
  // Render control based on blend values:
  // Scene 1: Render when transitioning out (hdriBlend < 1)
  // Scene 2: Render when transitioning in/out (hdriBlend > 0 and scene3Blend < 1)
  // Scene 3: Render when transitioning in (scene3Blend > 0)
  // Keep Scene 1 always mounted so baked shadows persist, just don't render when not needed
  const shouldMountScene1 = true
  const shouldRenderScene1 = hdriBlend < 1
  const shouldRenderScene2 = hdriBlend > 0 && scene3Blend < 1
  const shouldRenderScene3 = scene3Blend > 0

  useEffect(() => {
    if (dirLight1Ref.current && !cameraHelper1Ref.current) {
      cameraHelper1Ref.current = new CameraHelper(dirLight1Ref.current.shadow.camera)
    }
  }, [])

  // Scene 1 Content: Launch Platform with Environment
  const scene1Content = useMemo(() => {
    // Calculate POI/TAR positions based on gantry position
    const gantryPosition = [0, -1.31, 0]
    const gantryRotation = [0, Math.PI / 0.765, 0]
    const positions = calculatePoiTarPositions(gantryPosition, gantryRotation)
    
    return (
      <>

        <Environment
          files={['/textures/hdri/sky.webp', '/textures/hdri/sky-gainmap.webp', '/textures/hdri/sky.json']}
          background
          // backgroundIntensity={hdriControls.backgroundIntensity}
          backgroundIntensity={1.0}
          environmentIntensity={1.0}
        />
        
        <directionalLight 
          ref={dirLight1Ref}
          position={[10, 10, -5]}  
          intensity={5}
          color={lightControls.lightColor}
          castShadow 
          shadow-mapSize-width={2048} 
          shadow-mapSize-height={2048} 
          shadow-camera-far={50}
          shadow-camera-left={-10}
          shadow-camera-right={34}
          shadow-camera-top={25}
          shadow-camera-bottom={-15}
          shadow-bias={-0.0024}
          shadow-radius={5}
        />
        {lightControls.showShadowHelper && cameraHelper1Ref.current && <primitive object={cameraHelper1Ref.current} />}
        <FadingGround  />
        
        <CubeCamera resolution={128} frames={1} position={[0, 2, 0]} near={0.1} far={1000}>
          {(texture) => (
            <>
              <Environment 
                map={texture}
                environmentIntensity={1.0}
                background={false}
              />
              
            </>
          )}
        </CubeCamera>
              
        <group position={[0, 0, 0]}>
          <Instances rotation={[0, Math.PI / 1.86, 0]} receiveShadow castShadow selectiveBloomEnabled={false}>
            <BlackArrow castShadow receiveShadow hdriBlend={hdriBlend}/>
          </Instances>
        </group>

        <GantryInstances>
          <Gantry position={[0, -1.31, 0]} rotation={[0, Math.PI / 0.765, 0]} castShadow receiveShadow/>
        </GantryInstances>

      </>
    )
  }, [])

  // Scene 2 Content: Rocket with all parts assembled + Studio HDRI
  const scene2Content = useMemo(() => (
    <>
      {/* Studio HDRI */}
      <Environment
        files={['/textures/hdri/studio_small_01_1k.webp', '/textures/hdri/studio_small_01_1k-gainmap.webp', '/textures/hdri/studio_small_01_1k.json']}
        background={false}
        environmentIntensity={1}
        environmentRotation={[0, 1.8, 0]}
      />

      <Instances rotation={[0, Math.PI / 1.86, 0]} selectiveBloomEnabled={false}>
        <BlackArrow 
          hdriBlend={hdriBlend}
          scene3Blend={0}
        />
      </Instances>
    </>
  ), [hdriBlend])
  
  // Scene 3 Content: Prospero only + Earth Environment
  const scene3Content = useMemo(() => (
    <>
      {/* Earth Environment */}
      <Environment
        files={['/textures/hdri/earth2.webp', '/textures/hdri/earth2-gainmap.webp', '/textures/hdri/earth2.json']}
        background={true}
        backgroundBlurriness={0}
        backgroundIntensity={1}
        backgroundRotation={earthRotation}
        environmentIntensity={5}
        environmentRotation={earthRotation}
      />

      <Instances rotation={[0, Math.PI / 1.86, 0]} selectiveBloomEnabled={false}>
        <BlackArrow 
          hdriBlend={hdriBlend}
          scene3Blend={1}
        />
      </Instances>
    </>
  ), [hdriBlend, earthRotation])
  
  // ============================================================================
  // CAMERA CONTROL SYSTEM
  // ============================================================================
  // View-based camera locking: Controls are disabled in Scene 2 default view
  // (hdriBlend >= 1 && !isAtPOI) and enabled everywhere else.
  // 
  // Uses refs (sceneControlsRef, allowCameraControlRef) instead of state to avoid
  // re-triggering effects when other component state changes (e.g., rocket rotation).
  // The effect only runs when hdriBlend or isAtPOI changes - true view transitions.
  //
  // Event listener for 'scene-camera-move' uses empty dependency array to set up once,
  // then reads from refs during animation to get current control state.
  // ============================================================================

  // Determine if camera control is allowed based on current view
  useEffect(() => {
    // Scene 2 default view: NO control
    // Scene 1: YES control
    // At POI zoom: YES control
    const isScene2Default = hdriBlend >= 1 && !isAtPOI
    const newAllowControl = !isScene2Default
    
    // Update ref for closures to read current value
    allowCameraControlRef.current = newAllowControl
    
    // Apply immediately to controls if available (don't wait for state update)
    if (sceneControlsRef.current) {
      sceneControlsRef.current.enabled = newAllowControl
    }
  }, [hdriBlend, isAtPOI])
  
  // Handle scene camera movements
  useEffect(() => {
    // Create the handler inside the effect so it has access to current refs
    const handleSceneCameraMove = (event) => {
      const { targetPosition, targetLookAt, duration = 2000, onComplete } = event.detail
      const controls = sceneControlsRef.current
      
      if (!controls || !targetPosition || !targetLookAt) {
        return
      }
      
      // For instant movement (duration 0), set position immediately
      if (duration === 0) {
        if (controls.setLookAt) {
          controls.setLookAt(targetPosition.x, targetPosition.y, targetPosition.z, targetLookAt.x, targetLookAt.y, targetLookAt.z)
        } else {
          controls.camera.position.copy(targetPosition)
          if (controls.setPosition) {
            controls.setPosition(targetPosition.x, targetPosition.y, targetPosition.z)
          }
          controls.setTarget(targetLookAt.x, targetLookAt.y, targetLookAt.z)
        }
        controls.camera.updateMatrixWorld()
        if (onComplete) {
          onComplete()
        }
        return
      }
      
      // Set animation state
      setIsAnimating(true)
      
      // Temporarily disable CameraControls
      controls.enabled = false
      
      // Store original limits
      const originalMaxDistance = controls.maxDistance
      const originalMinDistance = controls.minDistance
      
      // Temporarily increase distance limits for launch camera movement
      controls.maxDistance = 100
      controls.minDistance = 1
      
      // Get current camera state
      const startPosition = controls.camera.position.clone()
      const startTarget = new THREE.Vector3()
      controls.getTarget(startTarget)
      
      // Animation start time
      let startTime = performance.now()
      
      const animate = (currentTime) => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / duration, 1)
        
        // Smooth easing
        const easedProgress = progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2
        
        // Interpolate position
        const currentPosition = startPosition.clone().lerp(targetPosition, easedProgress)
        
        // Interpolate target
        const currentTarget = startTarget.clone().lerp(targetLookAt, easedProgress)
        
        // Use CameraControls setLookAt if available, otherwise set position and target separately
        if (controls.setLookAt) {
          controls.setLookAt(currentPosition.x, currentPosition.y, currentPosition.z, currentTarget.x, currentTarget.y, currentTarget.z)
        } else {
          // Fallback: set position and target separately
          controls.camera.position.copy(currentPosition)
          if (controls.setPosition) {
            controls.setPosition(currentPosition.x, currentPosition.y, currentPosition.z)
          }
          controls.setTarget(currentTarget.x, currentTarget.y, currentTarget.z)
        }
        
        // Force update the camera matrix after position change
        controls.camera.updateMatrixWorld()
        
        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          // Set final position and target
          if (controls.setLookAt) {
            controls.setLookAt(targetPosition.x, targetPosition.y, targetPosition.z, targetLookAt.x, targetLookAt.y, targetLookAt.z)
          } else {
            controls.camera.position.copy(targetPosition)
            if (controls.setPosition) {
              controls.setPosition(targetPosition.x, targetPosition.y, targetPosition.z)
            }
            controls.setTarget(targetLookAt.x, targetLookAt.y, targetLookAt.z)
          }
          
          // Re-enable CameraControls (but respect the current view's control state)
          // Read from ref to get the current allowCameraControl value
          controls.enabled = allowCameraControlRef.current
          
          // Restore original distance limits
          controls.maxDistance = originalMaxDistance
          controls.minDistance = originalMinDistance
          
          // Set animation state
          setIsAnimating(false)
          
          // Call completion callback
          if (onComplete) {
            onComplete()
          }
        }
      }
      
      requestAnimationFrame(animate)
    }
    
    window.addEventListener('scene-camera-move', handleSceneCameraMove)
    
    return () => {
      window.removeEventListener('scene-camera-move', handleSceneCameraMove)
    }
  }, [])

  // Callback to capture camera and controls references when scenes are ready
  const handleScenesReady = (scenesData) => {
    sceneControlsRef.current = scenesData.controls
    
    // Call the original onScenesReady prop if provided
    if (onScenesReady) {
      onScenesReady(scenesData)
    }
  }

  return (
    <>
      {/* Triple Scene Manager - renders three scenes to textures */}
            
      <TripleSceneManager 
        scene1Content={scene1Content}
        scene2Content={scene2Content}
        scene3Content={scene3Content}
        onScene1Texture={setScene1Texture}
        onScene2Texture={setScene2Texture}
        onScene3Texture={setScene3Texture}
        mountScene1={shouldMountScene1}
        renderScene1={shouldRenderScene1}
        renderScene2={shouldRenderScene2}
        renderScene3={shouldRenderScene3}
        onScenesReady={handleScenesReady}
        hdriBlend={hdriBlend}
        scene3Blend={scene3Blend}
        isAnimating={isAnimating}
        isAtPOI={isAtPOI}
        activePOI={activePOI}
        cameraZoom={cameraZoom}
        shouldBakeShadows={shouldBakeShadows}
        cameraRotation={cameraRotation}
      />
      
      {/* Full screen plane with fading textures - follows camera */}
      {scene1Texture && scene2Texture && scene3Texture && (
        <ScreenPlane 
          texture1={scene1Texture} 
          texture2={scene2Texture}
          texture3={scene3Texture}
          fadeProgress={hdriBlend}
          scene3Progress={scene3Blend}
          selectiveBloomEnabled={selectiveBloomEnabled}
        />
      )}
      
      {/* Fallback during loading: Show scene1 and scene2 if scene3 not ready */}
      {scene1Texture && scene2Texture && !scene3Texture && (
        <ScreenPlane 
          texture1={scene1Texture} 
          texture2={scene2Texture}
          texture3={scene1Texture}
          fadeProgress={hdriBlend}
          scene3Progress={0}
          selectiveBloomEnabled={selectiveBloomEnabled}
        />
      )}
      
      {/* Fallback: Show just scene1 if scene2/3 not ready */}
      {scene1Texture && !scene2Texture && (
        <ScreenPlane 
          texture1={scene1Texture} 
          texture2={scene1Texture}
          texture3={scene1Texture}
          fadeProgress={0}
          scene3Progress={0}
          selectiveBloomEnabled={selectiveBloomEnabled}
        />
      )}
    </>
  )
}

export default TripleSceneContents